/** Whether two source values name the same thing: exact, or ignoring case for providers that treat names so. */
function isSameSourceValue(
  left: unknown,
  right: unknown,
  isCaseInsensitive: boolean,
): boolean {
  if (
    !isCaseInsensitive ||
    typeof left !== 'string' ||
    typeof right !== 'string'
  ) {
    return left === right;
  }
  return left.toLowerCase() === right.toLowerCase();
}

/**
 * The message for a restored value checked against the rows the source offers
 * now, or `undefined` when it passes.
 *
 * @remarks
 * The row is found by `id`. A missing row blocks only when the page is
 * complete, because on an incomplete page absence proves nothing. A found row
 * blocks when any of `sourceFields` differs. Fields outside `sourceFields`,
 * such as display text, are not compared. With `isCaseInsensitive`, string
 * values, `id` included, are compared ignoring case. Other values are always
 * compared with `===`.
 *
 * @typeParam TValue - The form value type, which carries the `id` the row is found by.
 * @typeParam TItem - The type of the rows the source offers.
 * @param input - The rows, whether the page is complete, the fields to compare, the subject named in the message, and the submitted value.
 * @returns A message for `addError`, or `undefined` when the value matches the source or its absence proves nothing.
 */
export function describeSourceMismatch<
  TValue extends { readonly id: string },
  TItem extends TValue,
>(input: {
  readonly isCaseInsensitive: boolean;
  readonly isComplete: boolean;
  readonly items: readonly TItem[];
  readonly sourceFields: readonly (keyof TValue)[];
  readonly subject: string;
  readonly submittedValue: TValue;
}): string | undefined {
  const offeredRow = input.items.find(item =>
    isSameSourceValue(
      item.id,
      input.submittedValue.id,
      input.isCaseInsensitive,
    ),
  );
  if (offeredRow === undefined) {
    return input.isComplete
      ? `The selected ${input.subject} is no longer offered. Choose another ${input.subject}.`
      : undefined;
  }
  const hasChanged = input.sourceFields.some(
    key =>
      !isSameSourceValue(
        input.submittedValue[key],
        offeredRow[key],
        input.isCaseInsensitive,
      ),
  );
  return hasChanged
    ? `The selected ${input.subject} has changed. Choose it again.`
    : undefined;
}
